/** Pesquisa global: encontrar um elemento por GlobalId, nome ou classe.
 *
 *  Hoje, para chegar a um elemento é preciso saber onde ele está na
 *  árvore, ou montar um filtro. Quem tem um GlobalId vindo de um
 *  relatório de IDS, de um tópico BCF ou de um email não tem por onde
 *  começar.
 *
 *  Este ficheiro tem só a parte que decide o que casa e por que ordem.
 *  É pura de propósito: é aqui que os erros se escondem, e assim
 *  testa-se sem modelo nenhum carregado.
 */

export interface EntradaPesquisa {
  modelId: string
  /** O id do elemento dentro do modelo. */
  localId: number
  /** A classe IFC, em maiúsculas, como o Fragments a devolve. */
  ifcClass: string
  nome: string | null
  guid: string | null
}

export interface ResultadoPesquisa extends EntradaPesquisa {
  /** Quanto melhor a correspondência, maior. Só serve para ordenar. */
  pontuacao: number
  /** Onde é que casou, para a interface poder dizê-lo. */
  motivo: 'guid' | 'nome' | 'classe'
}

/** Um GlobalId do IFC tem 22 caracteres de um alfabeto próprio.
 *
 *  Reconhecê-lo permite dar-lhe prioridade absoluta: quem cola um
 *  GlobalId quer aquele elemento e não outro parecido. */
export function pareceGuid(texto: string): boolean {
  return /^[0-9A-Za-z_$]{22}$/.test(texto.trim())
}

/** Tira acentos e baixa a caixa, para "Parede Exterior" casar com
 *  "parede exterior" e com "PAREDE EXTERIOR". */
export function normalizar(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim()
}

/** Pontuação de um texto contra a procura.
 *
 *  A escala é deliberadamente grosseira: igual vale mais do que
 *  começa por, que vale mais do que contém. Refinar mais do que isto
 *  seria inventar precisão que não temos. */
function pontuarTexto(alvo: string, procura: string): number {
  if (!alvo) return 0
  if (alvo === procura) return 100
  if (alvo.startsWith(procura)) return 60
  if (alvo.includes(procura)) return 30
  return 0
}

/**
 * Procura nas entradas e devolve os melhores resultados.
 *
 * Ordem de prioridade:
 *  1. GlobalId igual, que é sempre o que a pessoa quer quando o cola;
 *  2. nome;
 *  3. classe IFC.
 *
 * A classe vale menos do que o nome porque procurar "wall" com mil
 * paredes no modelo devolvia mil resultados iguais e enterrava a
 * parede que se chama mesmo "Wall-01".
 */
export function procurar(
  entradas: readonly EntradaPesquisa[],
  consulta: string,
  limite = 30,
): ResultadoPesquisa[] {
  const procura = normalizar(consulta)
  if (procura.length < 2) return []

  // Um GlobalId colado é uma pergunta fechada: ou está no modelo ou
  // não está, e mais nada interessa. Sem este atalho, um GUID que por
  // acaso contivesse "wall" trazia paredes atrás.
  const ehGuid = pareceGuid(consulta)
  if (ehGuid) {
    const exatos = entradas
      .filter((e) => e.guid === consulta.trim())
      .map((e) => ({ ...e, pontuacao: 1000, motivo: 'guid' as const }))
    if (exatos.length > 0) return exatos.slice(0, limite)
  }

  const resultados: ResultadoPesquisa[] = []
  for (const entrada of entradas) {
    const porGuid = entrada.guid
      ? pontuarTexto(normalizar(entrada.guid), procura)
      : 0
    const porNome = entrada.nome
      ? pontuarTexto(normalizar(entrada.nome), procura)
      : 0
    const porClasse = pontuarTexto(normalizar(entrada.ifcClass), procura)

    // A classe entra a peso reduzido: ver a nota no cabeçalho.
    const candidatos: [number, ResultadoPesquisa['motivo']][] = [
      [porGuid * 2, 'guid'],
      [porNome, 'nome'],
      [porClasse * 0.2, 'classe'],
    ]
    let melhor = 0
    let motivo: ResultadoPesquisa['motivo'] = 'nome'
    for (const [pontos, qual] of candidatos) {
      if (pontos > melhor) {
        melhor = pontos
        motivo = qual
      }
    }
    if (melhor > 0) {
      resultados.push({ ...entrada, pontuacao: melhor, motivo })
    }
  }

  resultados.sort(
    (a, b) =>
      b.pontuacao - a.pontuacao ||
      // Desempate estável: por classe e depois por id, para a lista não
      // dançar entre duas procuras iguais.
      a.ifcClass.localeCompare(b.ifcClass) ||
      a.localId - b.localId,
  )
  return resultados.slice(0, limite)
}

/** O texto que descreve um resultado numa linha. */
export function descrever(r: ResultadoPesquisa): string {
  const classe = r.ifcClass.replace(/^IFC/, '')
  return r.nome ? `${r.nome} · ${classe}` : classe
}
